import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Linkedin, Youtube, MessageCircle } from "lucide-react";

/**
 * SMIN unified footer — rendered on every marketing / static page.
 * Kept intentionally identical to the LandingPage footer so that every
 * public page shares the same brand surface.
 */
const COLS = [
  {
    title: "Product",
    links: [
      { label: "Features",      to: "/features" },
      { label: "Modules",       to: "/modules" },
      { label: "Reporting",     to: "/reporting" },
      { label: "Integrations",  to: "/integrations" },
      { label: "AI Assistant",  to: "/ai-assistant" },
    ],
  },
  {
    title: "How it Works",
    links: [
      { label: "Workflow",             to: "/workflow" },
      { label: "Use Cases",            to: "/use-cases" },
      { label: "System Requirements",  to: "/system-requirements" },
      { label: "Security",             to: "/security" },
      { label: "How it works",         to: "/how-it-works" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Documentation", to: "/documentation" },
      { label: "Tutorials",     to: "/tutorials" },
      { label: "Blog",          to: "/blog" },
      { label: "Case Studies",  to: "/case-studies" },
      { label: "FAQ",           to: "/faq" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us",       to: "/about" },
      { label: "Careers",        to: "/careers" },
      { label: "Contact Us",     to: "/contact" },
      { label: "Support",        to: "/support" },
      { label: "Privacy Policy", to: "/privacy" },
    ],
  },
];

export default function SminFooter() {
  return (
    <footer className="bg-neutral-50 text-neutral-800 border-t border-neutral-200 mt-6" data-testid="smin-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-1">
          <Link to="/landing" className="inline-flex items-center" data-testid="footer-brand">
            <img
              src="/branding/smin-logo-horizontal.png?v=9"
              alt="SMIN"
              className="h-14 w-auto object-contain"
            />
          </Link>
          <p className="mt-4 text-xs text-neutral-600 leading-relaxed">
            Modern mine planning platform for geologists and mine engineers. Plan smarter,
            optimise better, report faster.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <a
              href="https://www.linkedin.com/company/shanvi-resources"
              target="_blank" rel="noreferrer"
              aria-label="LinkedIn"
              data-testid="footer-linkedin"
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-neutral-200 bg-white hover:border-[#4CAF50] hover:text-[#4CAF50] transition"
            >
              <Linkedin className="h-4 w-4" />
            </a>
            <a
              href="https://www.youtube.com/@shanviresources"
              target="_blank" rel="noreferrer"
              aria-label="YouTube"
              data-testid="footer-youtube"
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-neutral-200 bg-white hover:border-[#4CAF50] hover:text-[#4CAF50] transition"
            >
              <Youtube className="h-4 w-4" />
            </a>
            <a
              href="https://wa.me/919938872207"
              target="_blank" rel="noreferrer"
              aria-label="WhatsApp"
              data-testid="footer-whatsapp"
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-neutral-200 bg-white hover:border-[#4CAF50] hover:text-[#4CAF50] transition"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
          </div>
        </div>

        {COLS.map((col) => (
          <div key={col.title}>
            <div className="text-sm font-semibold mb-4 text-neutral-900">{col.title}</div>
            <ul className="space-y-2.5 text-xs text-neutral-600">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="hover:text-[#4CAF50] transition"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <div>© 2026 SMIN. All rights reserved. A Product of Shanvi Resources.</div>
          <div className="flex items-center gap-6">
            <a
              href="mailto:info@sresources.co"
              className="hover:text-[#4CAF50] transition inline-flex items-center gap-1.5"
              data-testid="footer-email"
            >
              <Mail className="h-3.5 w-3.5" /> info@sresources.co
            </a>
            <a
              href="tel:+918079989999"
              className="hover:text-[#4CAF50] transition inline-flex items-center gap-1.5"
              data-testid="footer-phone"
            >
              <Phone className="h-3.5 w-3.5" /> +91 80799 89999
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
